import React from 'react';
import { useNavigate } from 'react-router-dom';

const ForSchools: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #6C5CE7, #5B4BC4)',
        color: 'white',
        padding: '80px 40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: '3rem',
          fontWeight: '800',
          marginBottom: '20px'
        }}>
          Scriblish for Schools
        </h1>
        <p style={{
          fontSize: '1.3rem',
          maxWidth: '800px',
          margin: '0 auto 30px',
          lineHeight: '1.6'
        }}>
          An educational creative writing platform designed for tweens
        </p>
        <button
          onClick={() => navigate('/signup')}
          style={{
            background: 'white',
            color: '#6C5CE7',
            border: 'none',
            borderRadius: '10px',
            padding: '16px 40px',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}
        >
          Get Started Free
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px'
      }}>
        {/* What is Scriblish */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '20px'
          }}>
            📚 What is Scriblish?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: '#666',
            marginBottom: '20px'
          }}>
            Scriblish is a <strong>Middles school educational platform</strong> that helps students develop creative writing skills through structured assignments, real-time teacher feedback, and engaging writing tools.
          </p>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: '#666'
          }}>
            <strong>This is NOT a gaming site.</strong> While we use gamification elements (points, achievements) to motivate student engagement, Scriblish is designed specifically for literacy education and classroom use.
          </p>
        </section>

        {/* Features Grid */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '30px'
          }}>
            ✨ Educational Features
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                icon: '👩‍🏫',
                title: 'Teacher Dashboard',
                description: 'Manage classes, create assignments, track student progress, and provide detailed feedback on student work.'
              },
              {
                icon: '📝',
                title: 'Writing Assignments',
                description: 'Create custom writing prompts with word limits, due dates, and specific requirements for classroom projects.'
              },
              {
                icon: '💬',
                title: 'Teacher Feedback',
                description: 'Provide public comments, private notes, and constructive suggestions on student writing. Return work for revision when needed.'
              },
              {
                icon: '📊',
                title: 'Progress Tracking',
                description: 'Monitor student writing activity, word counts, and assignment completion. View submission status in real-time.'
              },
              {
                icon: '✍️',
                title: 'Writing Tools',
                description: 'Story planning, chapter organization, and formatting tools help students structure their creative writing.'
              },
              {
                icon: '🎓',
                title: 'Classroom Management',
                description: 'Easy class setup with join codes. Students submit work directly to class assignments for teacher review.'
              }
            ].map((feature, idx) => (
              <div
                key={idx}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#333',
                  marginBottom: '10px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: '#666'
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Educational Use */}
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '60px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '20px'
          }}>
            🏫 Designed for Education
          </h2>
          <ul style={{
            fontSize: '1.1rem',
            lineHeight: '2',
            color: '#666',
            paddingLeft: '30px'
          }}>
            <li><strong>Standards-Aligned:</strong> Supports Common Core writing standards for narrative, creative, and expository writing</li>
            <li><strong>Teacher-Controlled:</strong> Teachers create accounts with .edu emails, manage classes, and review all student work</li>
            <li><strong>Safe Environment:</strong> Student work is private to their class and teacher only</li>
            <li><strong>Engagement Through Gamification:</strong> XP points and achievements motivate students without distracting from learning objectives</li>
            <li><strong>No Gaming Content:</strong> No violent content, multiplayer games, or entertainment-focused activities</li>
            <li><strong>Educational Purpose:</strong> Every feature supports literacy development and writing skill progression</li>
          </ul>
        </section>

        {/* FAQ */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '30px'
          }}>
            ❓ Frequently Asked Questions
          </h2>
          
          {[
            {
              q: 'Why is Scriblish being blocked as a gaming site?',
              a: 'Some web filters incorrectly categorize Scriblish as gaming due to our use of motivational elements like XP and achievements. We are working with filtering services to recategorize the site as educational. Scriblish is a writing platform, not a game.'
            },
            {
              q: 'Is Scriblish appropriate for school use?',
              a: 'Yes! Scriblish is specifically designed for tween classroom use. Teachers manage classes, create assignments, and review student work. All content is educational and supports literacy standards.'
            },
            {
              q: 'How do students access Scriblish?',
              a: 'Teachers create classes and share join codes with students. Students create accounts and join their teacher\'s class to receive assignments and submit writing.'
            },
            {
              q: 'What about student privacy?',
              a: 'Student work is only visible to the student and their assigned teacher. We do not share student data or content publicly.'
            },
            {
              q: 'Is Scriblish free?',
              a: 'Yes! Scriblish is currently free for all teachers and students.'
            }
          ].map((faq, idx) => (
            <div
              key={idx}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '25px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#333',
                marginBottom: '10px'
              }}>
                {faq.q}
              </h3>
              <p style={{
                fontSize: '1rem',
                lineHeight: '1.6',
                color: '#666',
                margin: 0
              }}>
                {faq.a}
              </p>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{
          background: 'linear-gradient(135deg, #6C5CE7, #5B4BC4)',
          borderRadius: '12px',
          padding: '50px 40px',
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '15px'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            marginBottom: '25px',
            opacity: 0.9
          }}>
            Join teachers nationwide using Scriblish for creative writing education
          </p>
          <button
            onClick={() => navigate('/signup')}
            style={{
              background: 'white',
              color: '#6C5CE7',
              border: 'none',
              borderRadius: '10px',
              padding: '16px 40px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
          >
            Sign Up Free
          </button>
        </section>

        {/* Contact */}
        <section style={{
          textAlign: 'center',
          marginTop: '60px',
          padding: '30px',
          background: 'white',
          borderRadius: '12px'
        }}>
          <h3 style={{
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '10px'
          }}>
            Questions or Need Help with Web Filtering?
          </h3>
          <p style={{
            fontSize: '1rem',
            color: '#666',
            marginBottom: '15px'
          }}>
            Contact us if your school's web filter is blocking Scriblish
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#6C5CE7',
            fontWeight: '600'
          }}>
            tranta@scriblish.com
          </p>
        </section>
      </div>
    </div>
  );
};

export default ForSchools;